import React, { useState, useContext, useEffect, useMemo, useRef } from "react";
import { AuthContext } from './../context/AuthContext';
import { getApps, getAdminApps, getGodApps, doorbell, getSIM, getSIMsList } from "../../api/api";
import './AppsMenu.css';

// Struttura unica dei gruppi, condivisa da layout mobile e desktop.
const MENU_GROUPS = [
  { id: "lavoro", title: "Lavoro", items: ["Order Tracking", "Statistiche", "Turni"] },
  { id: "team", title: "Team", items: ["Rubrica", "Meeting room"] },
  { id: "crescita", title: "Crescita e risorse", items: ["Academy", "Rewards", "Link utili"] }
];
const ACCOUNT_NAME = "Account";
const FEATURED_NAME = "Order Tracking";
const FALLBACK_ICON = "apps";
const DESKTOP_BREAKPOINT = 768;

const normalize = (value) => (value || "").trim().toLowerCase();
const isNamed = (name) => (entry) => normalize(entry.app.name) === normalize(name);

const useMinWidth = (px) => {
  const query = `(min-width: ${px}px)`;
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);
  return matches;
};

const Icon = ({ name, className = "" }) => (
  <span className={`material-symbols-outlined ${className}`} aria-hidden="true">{name}</span>
);

const Modal = (props) => {
  const [isDark, setIsDark] = useState(true);
  useEffect(() => {
    const link = document.getElementById("theme-css");
    if (!link) return undefined;
    const sync = () => setIsDark(!(link.getAttribute("href") || "").includes("theme-light"));
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(link, { attributes: true, attributeFilter: ["href"] });
    return () => observer.disconnect();
  }, []);
  const companyID = props.userDetails && props.userDetails.Company ? props.userDetails.Company[0].dataID : null;
  const role = props.userDetails && props.userDetails.role;
  const [apps, setApps] = useState([]);
  const [adminApps, setAdminApps] = useState([]);
  const [godApps, setGodApps] = useState([]);
  const [SIMlist, setSIMlist] = useState([]);
  const [query, setQuery] = useState("");
  const [simLoading, setSimLoading] = useState(false);
  const { user } = useContext(AuthContext);
  const searchRef = useRef(null);
  const isDesktop = useMinWidth(DESKTOP_BREAKPOINT);

  const loadApps = async () => {
    const response = await getApps(companyID);
    setApps(response.data.data.getAppListing.edges);
    if (role <= 1) {
      const adminResponse = await getAdminApps(companyID);
      setAdminApps(adminResponse.data.data.getAppListing.edges);
    }
    if (role <= 0) {
      const godResponse = await getGodApps(companyID);
      setGodApps(godResponse.data.data.getAppListing.edges);
    }
  };

  const generateSIM = async (seriale_sim, carrier, size) => {
    setSimLoading(true);
    try {
      const response = await getSIM({ seriale_sim, carrier, size });
      if (response.status === 200) {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank"); // Apre il PDF in una nuova scheda
        setQuery("");
      } else {
        console.error("Error generating SIM:", response);
      }
    } catch (error) {
      console.error("Error generating SIM:", error);
    } finally {
      setSimLoading(false);
    }
  };

  const fetchSIMsList = async () => {
    const response = await getSIMsList();
    setSIMlist(response.data.data.getSimListing.edges);
  };

  useEffect(() => {
    if (companyID) {
      loadApps();
      fetchSIMsList();
    }
  }, [companyID]);

  // Ctrl/Cmd+K porta il focus sulla ricerca, aprendo il menu se necessario.
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        const modal = document.getElementById("appsModal");
        const focusSearch = () => searchRef.current && searchRef.current.focus();
        if (modal && modal.classList.contains("show")) {
          focusSearch();
        } else if (modal && window.jQuery) {
          window.jQuery(modal).one("shown.bs.modal", focusSearch).modal("show");
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const entries = useMemo(() => [
    ...apps.map(edge => ({ app: edge.node, kind: "user" })),
    ...adminApps.map(edge => ({ app: edge.node, kind: "admin" })),
    ...godApps.map(edge => ({ app: edge.node, kind: "god" }))
  ], [apps, adminApps, godApps]);

  const accountEntry = entries.find(isNamed(ACCOUNT_NAME));
  const featuredEntry = isDesktop ? entries.find(isNamed(FEATURED_NAME)) : undefined;

  const groups = useMemo(() => {
    const rest = entries.filter(entry => entry !== accountEntry && entry !== featuredEntry);
    const knownNames = new Set(MENU_GROUPS.flatMap(group => group.items.map(normalize)));
    const result = MENU_GROUPS.map(group => ({
      ...group,
      entries: group.items.flatMap(name => rest.filter(isNamed(name)))
    }));
    // Voci non previste dai gruppi: non vengono perse, restano raggiungibili.
    const unknown = rest.filter(entry => !knownNames.has(normalize(entry.app.name)));
    result.push({ id: "altro", title: "Altro", entries: unknown.filter(entry => entry.kind === "user") });
    result.push({ id: "admin", title: "Amministrazione", entries: unknown.filter(entry => entry.kind !== "user") });
    return result;
  }, [entries, accountEntry, featuredEntry]);

  const term = normalize(query);
  const matches = (entry) => !term
    || normalize(entry.app.name).includes(term)
    || normalize(entry.app.subTitle).includes(term);

  const visibleGroups = groups
    .map(group => ({ ...group, entries: group.entries.filter(matches) }))
    .filter(group => group.entries.length > 0);
  const showAccount = accountEntry && matches(accountEntry);
  const showFeatured = featuredEntry && matches(featuredEntry);
  const noResults = !showAccount && !showFeatured && visibleGroups.length === 0;

  const linkProps = (app) => ({
    href: app.url,
    target: app.target,
    rel: app.target === "_blank" ? "noopener noreferrer" : undefined,
    onClick: normalize(app.name) === "meeting room"
      ? (e) => {
        e.preventDefault();
        doorbell("Campanello suonato da " + user);
        window.open(app.url, app.target);
      }
      : undefined
  });

  const iconClass = (kind) => (kind === "god" ? "am-icon am-icon--god" : "am-icon");
  const iconName = (app) => app.icon || FALLBACK_ICON;
  const initial = (user || "?").trim().charAt(0).toUpperCase();
  const isSimSerial = /^\d{19,20}$/.test(query.trim());

  const closeButton = (
    <button type="button" className="am-close" data-dismiss="modal" aria-label="Chiudi menu">
      <Icon name="close" />
    </button>
  );

  const searchField = (extraClass, showShortcut) => (
    <div className={`am-search ${extraClass}`}>
      <label htmlFor="quick-search" className="am-sr-only">Cerca nel menu</label>
      <Icon name="search" className="am-search-icon" />
      <input
        className="border-0"
        id="quick-search"
        ref={searchRef}
        type="text"
        autoComplete="off"
        placeholder="Cerca…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {isSimSerial ? (
        <div className="am-sim dropdown">
          <button
            type="button"
            className={`am-sim-btn dropdown-toggle${simLoading ? " am-sim-btn--loading" : ""}`}
            data-toggle="dropdown"
            aria-label="Genera SIM"
            disabled={simLoading}
          >
            <Icon name="sim_card" />
          </button>
          <div className="dropdown-menu dropdown-menu-right am-sim-menu">
            <div className="am-sim-menu-scroll">
              {SIMlist.map((edge, index) => (
                <a
                  key={index}
                  className="dropdown-item"
                  href="#"
                  onClick={(e) => { e.preventDefault(); generateSIM(query.trim(), edge.node.name, edge.node.size); }}
                >
                  {edge.node.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      ) : (
        showShortcut && !query && <kbd className="am-kbd" aria-hidden="true">Ctrl K</kbd>
      )}
    </div>
  );

  const accountCard = showAccount && (
    <a className="am-account" {...linkProps(accountEntry.app)}>
      <span className="am-avatar" aria-hidden="true">{initial}</span>
      <span className="am-account-text">
        <span className="am-account-title">Account</span>
        <span className="am-account-sub">Profilo e impostazioni</span>
      </span>
      <Icon name="chevron_right" className="am-chevron" />
    </a>
  );

  const emptyMessage = noResults && <p className="am-empty" role="status">Nessun risultato</p>;

  const mobileLayout = (
    <div className="am-page am-mobile">
      <div className="am-m-top">
        {searchField("am-search--mobile", false)}
        {closeButton}
      </div>
      {accountCard}
      {visibleGroups.map(group => (
        <section key={group.id} aria-labelledby={`am-group-${group.id}`}>
          <h2 className="am-group-title" id={`am-group-${group.id}`}>{group.title}</h2>
          <div className="am-card">
            {group.entries.map(entry => (
              <a key={entry.app.id} className="am-row" {...linkProps(entry.app)}>
                <span className={iconClass(entry.kind)}><Icon name={iconName(entry.app)} /></span>
                <span className="am-row-label">{entry.app.name}</span>
                <Icon name="chevron_right" className="am-chevron" />
              </a>
            ))}
          </div>
        </section>
      ))}
      {emptyMessage}
      <a href="/" rel="noopener noreferrer" className="am-logo"><img src="images/rebootLogo.png" alt="Reboot" /></a>
    </div>
  );

  const desktopLayout = (
    <div className="am-page am-desktop">
      <header className="am-d-header">
        <a href="/" rel="noopener noreferrer" className="am-d-logo"><img src="images/rebootLogo.png" alt="Reboot" /></a>
        {searchField("am-search--desktop", true)}
        <div className="am-d-close">{closeButton}</div>
      </header>
      <h1 className="am-d-title">Menu</h1>
      {(showFeatured || showAccount) && (
        <div className="am-featured-row">
          {showFeatured && (
            <a className="am-featured" {...linkProps(featuredEntry.app)}>
              <span className="am-featured-icon"><Icon name={iconName(featuredEntry.app)} /></span>
              <span className="am-featured-text">
                <span className="am-featured-title">{featuredEntry.app.name}</span>
                <span className="am-featured-sub">Segui lo stato degli ordini</span>
              </span>
              <span className="am-featured-cta">Apri <Icon name="chevron_right" /></span>
            </a>
          )}
          {showAccount && <div className="am-featured-account">{accountCard}</div>}
        </div>
      )}
      <div className="am-columns">
        {visibleGroups.map(group => (
          <section key={group.id} className="am-column" aria-labelledby={`am-group-${group.id}`}>
            <h2 className="am-group-title" id={`am-group-${group.id}`}>{group.title}</h2>
            <div className="am-column-list">
              {group.entries.map(entry => (
                <a key={entry.app.id} className="am-tile" {...linkProps(entry.app)}>
                  <span className={iconClass(entry.kind)}><Icon name={iconName(entry.app)} /></span>
                  <span className="am-row-label">{entry.app.name}</span>
                  <Icon name="chevron_right" className="am-chevron" />
                </a>
              ))}
            </div>
          </section>
        ))}
      </div>
      {emptyMessage}
    </div>
  );

  return (
    <div className={`modal fade am-modal${isDark ? " am-dark" : ""}`} id="appsModal" tabIndex={-1} role="dialog" aria-label="Menu principale" aria-hidden="true" data-backdrop="false">
      <div className="modal-dialog" role="document">
        <div className="modal-content">
          {isDesktop ? desktopLayout : mobileLayout}
        </div>
      </div>
    </div>
  );
};
export default Modal;
